import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';

/** Sets one field of a `T`, typed by the field. */
export type SetField<T> = <K extends keyof T>(key: K, value: T[K]) => void;

interface FormValues<T> {
  values: T;
  /** Sets one field. */
  set: SetField<T>;
  /** Sets several fields at once, for rules that move more than one together. */
  patch: (changes: Partial<T>) => void;
  setValues: Dispatch<SetStateAction<T>>;
}

/**
 * Form state plus the one-field setter that five forms had copied verbatim.
 *
 * `initial` is read once, on mount — which is the point. `ResourceFormShell`
 * mounts a form only after its record has loaded, so `useState` initialises
 * straight from it and no effect ever copies props into state. That pattern is
 * both what the React Compiler lint rejects and what causes the cascading
 * render it rejects it for.
 */
export function useFormValues<T extends object>(initial: T): FormValues<T> {
  const [values, setValues] = useState<T>(initial);

  const set = useCallback(
    <K extends keyof T>(key: K, value: T[K]) =>
      setValues((current) => ({ ...current, [key]: value })),
    [],
  );

  const patch = useCallback(
    (changes: Partial<T>) => setValues((current) => ({ ...current, ...changes })),
    [],
  );

  return useMemo(() => ({ values, set, patch, setValues }), [values, set, patch]);
}
